<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Customers</h2>
    </header>

    <!-- Table -->
    <Table
      toolbarHeading="Customer List"
      :withToolbar="true"
      :withSearch="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="customers"
      :fetch="fetchCustomers"
    >
      <template #table-toolbar>
        <button
          type="button"
          :disabled="!$canI.create"
          class="shared-table-toolbar-button"
          @click="showFormModal = true"
        >
          <VSvgIcon name="plus" class="h-5 mr-1" /> Add Customer
        </button>
      </template>

      <template #table-data-6="trData">
        <a
          v-if="trData.referral_code"
          class="text-blue-500 underline"
          :href="`https://ricoma.com/US/referral/${trData.referral_code}`"
          target="_blank"
        >
          {{ trData.referral_code }}
        </a>
        <span v-else>-</span>
      </template>

      <template #table-data-11="trData">
        {{ addDollarSymbol(trData['pending payout balance']) }}
      </template>

      <template #table-data-12="trData">
        {{ addDollarSymbol(trData['available credit']) }}
      </template>

      <template #table-data-13="trData">
        <div
          class="flex items-center justify-center"
          :class="[
            trData['calculated credit'] < 0 && 'text-red-500',
            trData['calculated credit'] > 0 && 'text-green-500',
          ]"
        >
          {{ addDollarSymbol(trData['calculated credit']) }}
          <TooltipInfo
            v-if="trData['calculated credit'] < 0"
            :classes="{
              popup: `${trData.trIndex === trData.tableLength - 1 && 'bottom'}`,
              icon: `-mt-1 ${trData['calculated credit'] < 0 ? 'text-ricoma-red-100' : ''}`,
            }"
          >
            <div class="text-black whitespace-no-wrap">
              {{
                `
                Calculated Credit [ ${addDollarSymbol(trData['pending payout balance'])} ]  =  
                Pending Payout Balance [ ${addDollarSymbol(trData['available credit'])} ]  +  
                Available Credit on Netsuite [ ${addDollarSymbol(trData['calculated credit'])} ]
                `
              }}
            </div>
            <div v-if="trData['pending payout balance'] < 0" class="text-ricoma-red-100">
              <b>NOTE:</b>
              We will add any negative "Pending Payout Balance" to the "Available Credit" and adjust
              the final balance to reflect the true credit.
            </div>
          </TooltipInfo>
        </div>
      </template>

      <template #table-data-actions="trData">
        <td class="shared-table-cells-cell">
          <div class="flex justify-center">
            <button
              class="mr-2 shared-table-cells-button round edit"
              :disabled="!$canI.update"
              @click="() => handleSelectEditableCustomer(trData)"
            >
              <VSvgIcon name="pencil" class="h-5" />
            </button>
            <button class="shared-table-cells-button round delete" :disabled="!$canI.delete">
              <VSvgIcon name="trash" class="h-5" />
            </button>
          </div>
        </td>
      </template>
    </Table>

    <!-- Form Modal -->
    <ModalPlus
      :title="editable ? 'Edit Customer' : 'Add Customer'"
      :show="showFormModal && !showPreviewModal && !showPreviewDtpModal && !showPreviewDtgModal"
      :onClose="handleClose"
      :withButtons="false"
      maxWidth="800px"
    >
      <!-- when dealing with multiple forms in one component move their state, validation, etc.. into the form components themselves -->
      <div class="pt-8">
        <Tabs v-if="editable" :tabs="tabs" @change="t => (tab = t)" />
        <div>
          <CustomerForm
            v-if="tab.order === 0"
            :editable="editable"
            :editableCustomer="editableCustomer"
            :payoutTags="payoutTags"
            :templates="templates"
            :initialValues="editableCustomer"
            @preview="handleShowPreview"
            @previewDtp="handleShowPreviewDtp"
            @previewDtg="handleShowPreviewDtg"
            @template="value => (template = value)"
            @template_dtp="value => (template_dtp = value)"
            @template_dtg="value => (template_dtg = value)"
            @close="handleClose"
          />
          <CustomerProductsForm
            v-else-if="tab.order === 1 && editable"
            :chromaTiers="chromaTiers"
            :products="products"
            :customerProducts="editableCustomerProducts"
            :customerId="editableCustomer.id"
            @filter="text => handleFilterProducts(text)"
            @update="products => (editableCustomerProducts = products)"
            @close="handleClose"
          />
        </div>
      </div>
    </ModalPlus>

    <!-- Preview Modal -->
    <Modal
      :show="showPreviewModal && !showFormModal"
      theme="dark"
      width="95%"
      maxWidth="1000px"
      @close="handleClosePreview"
    >
      <div class="relative" :style="{ paddingTop: '63%' }">
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden rounded-lg">
          <img :src="previewSrc" alt="" class="object-cover w-full h-full" />
        </div>
      </div>
    </Modal>
    <Modal
      :show="showPreviewDtpModal && !showFormModal"
      theme="dark"
      width="95%"
      maxWidth="1000px"
      @close="handleClosePreviewDtp"
    >
      <div class="relative" :style="{ paddingTop: '63%' }">
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden rounded-lg">
          <img :src="previewDtpSrc" alt="" class="object-cover w-full h-full" />
        </div>
      </div>
    </Modal>
    <Modal
      :show="showPreviewDtgModal && !showFormModal"
      theme="dark"
      width="95%"
      maxWidth="1000px"
      @close="handleClosePreviewDtg"
    >
      <div class="relative" :style="{ paddingTop: '63%' }">
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden rounded-lg">
          <img :src="previewDtgSrc" alt="" class="object-cover w-full h-full" />
        </div>
      </div>
    </Modal>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { format, parseISO } from 'date-fns';

import { safeNullDataset, addDollarSymbol } from '@/utils';

import VSvgIcon from '@/components/base/VSvgIcon';
import Modal from '@/components/shared/modal/Modal';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import TooltipInfo from '@/components/shared/tooltip/TooltipInfo';
import Tabs from '@/components/shared/Tabs';
import CustomerForm from '@/components/admin/CustomerForm';
import CustomerProductsForm from '@/components/admin/CustomerProductsForm';
import Table from '@/components/shared/table/Table';

const TABS = [
  { name: 'Customer', order: 0 },
  { name: "Customer's Products", order: 1 },
];

export default {
  name: 'AdminCustomers',
  components: {
    VSvgIcon,
    Modal,
    ModalPlus,
    TooltipInfo,
    Tabs,
    CustomerForm,
    CustomerProductsForm,
    Table,
  },
  data() {
    return {
      showFormModal: false,
      showPreviewModal: false,
      showPreviewDtpModal: false,
      showPreviewDtgModal: false,
      editableCustomer: {},
      editableCustomerProducts: [],
      payoutTags: [],
      tab: TABS[0],
      template: '',
      template_dtp: '',
      template_dtg: '',
      products: [],
    };
  },
  watch: {
    // reset tabs back to Customer on open/close modal
    showFormModal() {
      this.tab = { name: 'Customer', order: 0 };
    },
  },
  computed: mapState({
    customers: state => ({
      data: safeNullDataset(state)(['admin', 'customers', 'data']).map(c => ({
        subsidary: c.subsidiary_id == '3' ? 'EU' : 'US',
        'first name': c.first_name || '-',
        'last name': c.last_name || '-',
        'email address': c.email || '-',
        'phone #': c.phone || '-',
        'business name': c.company_name || '-',
        'netsuite id': c.netsuite_id || '-',
        'referral code': c.referral_code || '-',
        'referral payout tag': (c.referral_payout_tag && c.referral_payout_tag.name) || '-',
        'landing page template emb': c.template.name || '-',
        'landing page template dtp': c.template_dtp.name || '-',
        'landing page template dtg': c.template_dtg.name || '-',
        'pending payout balance': parseFloat(c.balance).toFixed(2),
        'available credit': parseFloat(c.available_credit).toFixed(2),
        'calculated credit':
          c.balance < 0
            ? parseFloat(c.balance + c.available_credit).toFixed(2)
            : parseFloat(c.available_credit).toFixed(2),
        'date created': format(parseISO(c.created_at), 'MM/dd/yyyy') || '-',
        ...c,
      })),
      meta: state.admin.customers.meta,
    }),
    templates: state => state.admin.templates.data,
    editable() {
      return this.editableCustomer && !!Object.keys(this.editableCustomer).length;
    },
    previewSrc() {
      return this.template ? this.template.preview : '';
    },
    previewDtpSrc() {
      return this.template_dtp ? this.template_dtp.preview : '';
    },
    previewDtgSrc() {
      return this.template_dtg ? this.template_dtg.preview : '';
    },
    tabs() {
      return TABS;
    },
    chromaTiers() {
      return ['Inspire', 'Plus', 'Luxe'];
    },
  }),
  methods: {
    addDollarSymbol: num => addDollarSymbol(num),
    async fetchCustomers(params) {
      return await this.$store.dispatch('admin/getCustomers', params);
    },
    async handleFilterProducts(text) {
      const params = { search: text, per_page: 1000000, includes: [''] };
      const updated = await this.$store.dispatch('admin/getProducts', params);
      this.products = updated.data;
    },
    handleShowPreview() {
      this.showFormModal = false;
      this.showPreviewModal = true;
    },
    handleClosePreview() {
      this.showFormModal = true;
      this.showPreviewModal = false;
    },
    handleShowPreviewDtp() {
      this.showFormModal = false;
      this.showPreviewDtpModal = true;
    },
    handleClosePreviewDtp() {
      this.showFormModal = true;
      this.showPreviewDtpModal = false;
    },
    handleShowPreviewDtg() {
      this.showFormModal = false;
      this.showPreviewDtgModal = true;
    },
    handleClosePreviewDtg() {
      this.showFormModal = true;
      this.showPreviewDtgModal = false;
    },
    async handleSendInvite(id) {
      try {
        await this.$http.post(`/admin/customers/${id}/reinvite`);
        this.$store.commit('admin/updateCustomer', { id, invite_sent_at: new Date() });
      } catch (error) {
        console.error({ ...error });
      }
    },
    handleSelectEditableCustomer(customer) {
      this.editableCustomer = customer;
      this.handleSetEditableCustomerProducts(customer.id);
      this.showFormModal = true;
    },
    async handleSetEditableCustomerProducts(id) {
      const { data } = await this.$http.get(`/admin/customers/${id}/products`);
      this.editableCustomerProducts = data;
    },
    handleClose() {
      this.editableCustomer = {};
      this.showFormModal = false;
    },
  },
  async mounted() {
    const { data: payoutTagsData } = await this.$http.get('/admin/referral-payout-tag/payout_tags');
    this.payoutTags = payoutTagsData;

    const templates = this.$store.state.admin.templates;
    if (!Object.keys(templates).length) {
      this.$store.dispatch('admin/getTemplates');
    }

    const { data: productsData } = await this.$http.get('/admin/products-all');
    this.products = productsData;
  },
  tColumns: [],
  created() {
    this.$options.tColumns = [
      { column: 'subsidary', isSort: true, orderBy: 'id' },
      { column: 'first name', isSort: true, orderBy: 'first_name' },
      { column: 'last name', isSort: true, orderBy: 'last_name' },
      { column: 'email address', isSort: true, orderBy: 'email' },
      { column: 'phone #', isSort: true, orderBy: 'phone' },
      { column: 'business name', isSort: true, orderBy: 'company_name' },
      { column: 'netsuite id', isSort: true, orderBy: 'netsuite_id' },
      { column: 'referral code', isSort: true, orderBy: 'referral_code' },
      { column: 'referral payout tag', isSort: false },
      { column: 'landing page template emb', isSort: false },
      { column: 'landing page template dtp', isSort: false },
      { column: 'landing page template dtg', isSort: false },
      { column: 'pending payout balance', isSort: false },
      { column: 'available credit', isSort: true, orderBy: 'available_credit' },
      { column: 'calculated credit', isSort: false },
      { column: 'date created', isSort: true, orderBy: 'created_at' },
      { column: 'actions', isSort: false },
    ];
  },
};
</script>
